import { ExternalProviderLinkModel } from "@insite/client-framework/Services/IdentityService";
import { CartModel, OrderModel } from "@insite/client-framework/Types/ApiModels";

interface BaseModel {
    description: string;
    name: string;
}

export interface ProductConfigDto {
    bom: BomDto[];
    id: string;
    configId: string;
    pricing: PricingDto;
    productId: string;
    response: ResponseDto;
    steps: StepDto[];
    tab: string;
    visualization: object;
}

export interface BomDto {
    attributes: { [key: string]: string };
    description: string;
    name: string;
    qty: string;
    subItems: BomDto[];
}

export interface PricingDto {
    fields: PricingFieldDto[];
    rows: PricingRowDto[];
    summary: PricingSummaryDto;
}

export interface PricingFieldDto {
    field: string;
    format: string;
    label: string;
    type: string;
}

export interface PricingRowDto {
    rows: PricingRowDto[];
    summary: PricingSummaryDto;
}

export interface PricingSummaryDto {
    ListPrice: string;
    NetPrice: string;
    Qty: number;
    Variant: string;
    descriptionColumn: string;
}

export interface ResponseDto {
    status: string;
    changed: ChangedDto[];
}

export interface ChangedDto extends BaseModel {
    newValue: string;
    newValueDescription: string;
    oldValue: string;
    oldValueDescription: string;
}

export interface StepDto extends BaseModel {
    available: boolean;
    current: boolean;
    rootGroup: RootGroupDto;
}

export interface RootGroupDto extends BaseModel {
    hasVisibleParameters: boolean;
    members: MemberDto[];
}

export interface MemberDto extends BaseModel {
    alwaysCommitted: boolean;
    committed: boolean;
    domain: DomainDto;
    hasVisibleParameters: boolean;
    isGroup: boolean;
    isParameter: boolean;
    members: MemberDto[];
    properties: PropertiesDto;
    value: string;
    valueDescription: string;
}

export interface PropertiesDto {
    css_colOffset?: string;
    css_colSpan?: string;
    css_hide: string;
    css_inputPlaceholder?: string;
    css_navigationLabel?: string;
    css_pairingNames?: string;
    css_rowName: string;
    guitype: string;
    hasDetailedView: boolean;
    hidden: string;
    tc_data_integration_output: string[];
    tc_guitype: string;
    tc_gui_input: string;
    tc_hidden_unless_role: string;
    tc_hide_results_from_results_tab: string[];
    css_hideTree: string;
    tc_info_text: string;
    tc_component_picture: string;
    tc_unit: string;
    tc_field_view_text?: string;
}

export interface DomainDto {
    elements: ElementDto[];
    name: string;
    max?: string;
    min?: string;
}

export interface ElementDto extends BaseModel {
    selected: boolean;
    state: string;
    properties?: PropertiesDto;
}

export interface ProductConfigModel {
    id: string;
    tab: string;
    pricing: PricingModel;
    productId: string;
    response: ResponseModel;
    steps: StepModel[];
    isRFQ: boolean;
    rfqReason: string;
}

export interface PricingModel {
    listPrice: string;
    netPrice: string;
    quantity: number;
    description: string;
}

export interface ResponseModel {
    status: string;
    changed?: ChangedModel[];
}

export interface ChangedModel extends BaseModel {
    newValue: string;
    newValueDescription: string;
    oldValue: string;
    oldValueDescription: string;
}

export interface StepModel extends BaseModel {
    current: boolean;
    groups: GroupModel[];
}

export interface GroupModel extends BaseModel {
    rows?: InputRowModel[];
}

export interface InputRowModel {
    rowName: string;
    inputs?: Array<InputModel | ImageInputModel>;
}

export interface InputModel {
    colOffset: number;
    colSpan: number;
    committed: boolean;
    defaultValue: ValueModel;
    description: string;
    disabled: boolean;
    groupName: string;
    max: number;
    min: number;
    name: string;
    rowName: string;
    type: InputType;
    values: Array<ValueModel | ImageValueModel>;
    tcInfoText: string;
    tooltip?: string;
    required: boolean;
    placeholder?: string;
    units?: string;
    value: string;
}

export interface ImageInputModel extends InputModel {
    src: string;
    alt: string;
    values: ImageValueModel[];
}

export interface ValueModel {
    description: string;
    name: string;
    state?: string;
}

export interface ImageValueModel extends ValueModel {
    src: string;
    alt: string;
}

export enum InputType {
    TEXT = "text",
    NUMBER = "number",
    TOGGLE = "toggle",
    RADIO = "radio",
    SELECT = "select",
    BUTTON = "button",
    DATE = "date",
    DATETIME = "datetime",
    IMAGE = "image",
    IMAGE_SELECT = "image-select",
}

export const isImageInput = (input: InputModel | ImageInputModel): input is ImageInputModel => {
    return "src" in input && "alt" in input;
};

export interface CustomOrderModel extends OrderModel {
    spares: ExtendedOptionModel[];
    options: ExtendedOptionModel[];
}

export interface CustomCartModel extends CartModel {
    spares: ExtendedOptionModel[];
    options: ExtendedOptionModel[];
}

export interface ExtendedOrderCollectionModel {
    spares: ExtendedOptionModel[];
    options: ExtendedOptionModel[];
}

export interface ExtendedOptionModel extends BaseModel {
    configId: string;
    description: string;
    id: string;
    orderNumber: string;
    quantity: number;
}

export interface ProductLineDto {
    id: string;
    name: string;
    properties: any;
    uri: string;
}

export interface ExternalSignInModel {
    id: string;
    externalProviders?: ExternalProviderLinkModel[];
}

export interface RecaptchaSettingsModel {
    siteKey: string;
}

export interface ThirdPartyScriptSettingsModel extends BaseModel {
    oneTrustScriptId: string;
}
